import { Checkbox, FormControl, InputLabel, MenuItem, Radio, Select, TextField } from '@mui/material';
import AudioMultipleChoice from 'components/AdminBooks/AudioWithMultichoice';
import MatchMaking from 'components/AdminBooks/PageToolbarModals/MatchMaking';
import PictureAssignment from 'components/Image/PictureAssignment';
import TextEditor from 'components/TextEditor';
import AudioPicker from 'features/forms/AudioPicker';
import { AssignmentFormFillInBlanksComp, AssignmentVideo } from 'features/forms/assignment';
import PictureAssignmentType from 'features/forms/assignment/picture';
import AudioRecorder from 'features/AudeoRecorderNew';
import { cloneDeep } from 'lodash';
import AssignmentPhotoCollage from 'modules/activityModule/photoCollage';
import AudioWithEssay from 'modules/activityModule/audioWithWrittenResponse/AudioWithEssay';
import { useAlertLoading } from 'components/UI/alertSakeBar/alertProvider';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import { handleGetAiOptionForAnswer } from 'store/vocabulary/thunks';
import { DURATIONS, QUESTION_TYPE_ACTIVITY_NON_NATIVE, imageUpload } from 'utils/config/reference';
import { filterDuplicateWords, removeCustomDuplicates } from 'utils/methods/math';
import styles from '../AssignmentBuilderTool.module.css';
import { SuperAdminQuestionExtras } from './superAdminQuestionExtras';

const AudioRecorderAny: any = AudioRecorder;
const AudioPickerAny: any = AudioPicker;
const PictureAssignmentAny: any = PictureAssignment;
const MatchMakingAny: any = MatchMaking;
const FillInBlanksAny: any = AssignmentFormFillInBlanksComp;
const AssignmentVideoAny: any = AssignmentVideo;
const PictureAssignmentTypeAny: any = PictureAssignmentType;
const AssignmentPhotoCollageAny: any = AssignmentPhotoCollage;

type BuilderQuestion = {
  id?: number | string;
  _uid?: string;
  type: string;
  question?: string;
  options?: any[];
  answer?: any;
  duration?: number | string;
  audio?: string;
  groupKey?: string;
  subQuestions?: BuilderQuestion[];
  [key: string]: any;
};

export type QuestionBuilderProps = {
  draft: BuilderQuestion;
  setDraft: React.Dispatch<React.SetStateAction<BuilderQuestion | null>>;
  onPatch: (patch: Partial<BuilderQuestion>) => void;
  onTypeChange: (type: string) => void;
  setButtonDisabled?: (value: boolean) => void;
  superAdminMode?: boolean;
  bookPages?: { pageId?: string | number; NumberMasked?: string }[];
  isStandardExamBook?: boolean;
  standards?: { abbr?: string; status?: boolean; state?: string; grade_level?: string }[];
  ebookGradeLevels?: { state?: string; grade_level?: string }[];
  enabledStateCodes?: string[];
};

function isInstructionType(type: string) {
  return ['match', 'filling', 'fillingImage', 'audioToImage', 'audioEssay', 'audioTrueFalse'].includes(type);
}

function BuilderSectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className={styles.builderSection}>
      <div className={styles.builderSectionTitle}>{title}</div>
      <div className={styles.builderSectionBody}>{children}</div>
    </section>
  );
}

function QuestionRichTextField({ draft, onPatch }: { draft: BuilderQuestion; onPatch: (patch: Partial<BuilderQuestion>) => void }) {
  const questionPlaceholder =
    draft.type === 'trueFalse'
      ? 'Write question here'
      : isInstructionType(draft.type)
        ? 'Type your instruction here...'
        : 'Type your question here...';
  return (
    <BuilderSectionCard title={isInstructionType(draft.type) ? 'Instruction' : 'Question'}>
      <TextEditor
        key={`question-editor-${draft._uid || draft.id || 'draft'}-${draft.type}`}
        handleChange={(value) => onPatch({ question: value })}
        placeholder={questionPlaceholder}
        value={draft.question || ''}
        autoFocus={false}
        disabled={false}
        containerClassName="question-ebook-text-field"
      />
    </BuilderSectionCard>
  );
}

function QuestionTypeSelector({ draft, onTypeChange }: { draft: BuilderQuestion; onTypeChange: (type: string) => void }) {
  return (
    <BuilderSectionCard title="Question Type">
      <FormControl fullWidth size="small">
        <InputLabel>Type</InputLabel>
        <Select
          label="Type"
          value={draft.type}
          onChange={(e) => onTypeChange(String(e.target.value))}
        >
          {QUESTION_TYPE_ACTIVITY_NON_NATIVE.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.type}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </BuilderSectionCard>
  );
}

function ChoiceOptionsEditor({
  draft,
  onPatch,
  selectionMode,
  fixedLength,
}: {
  draft: BuilderQuestion;
  onPatch: (patch: Partial<BuilderQuestion>) => void;
  selectionMode: 'single' | 'multiple';
  fixedLength?: number;
}) {
  const options = Array.isArray(draft.options) && draft.options.length ? draft.options : ['', '', '', ''];
  const selectedAnswers = Array.isArray(draft.answer) ? draft.answer.map(String) : [];
  const optionCount = fixedLength || options.length || 4;

  const updateOptions = (next: string[]) => {
    if (selectionMode === 'multiple') {
      const currentAnswers = Array.isArray(draft.answer) ? draft.answer.map(String) : [];
      onPatch({
        options: next,
        answer: currentAnswers.filter((value) => Number(value) < next.length),
      });
      return;
    }

    const currentAnswer = Number(draft.answer ?? 0);
    onPatch({
      options: next,
      answer: currentAnswer >= next.length ? 0 : currentAnswer,
    });
  };

  return (
    <BuilderSectionCard title="Answer Setup">
      {!fixedLength ? (
        <FormControl fullWidth size="small" sx={{ mb: 2, maxWidth: 220 }}>
          <InputLabel>Number of options</InputLabel>
          <Select
            value={optionCount}
            label="Number of options"
            onChange={(e) => {
              const nextLength = Number(e.target.value);
              const nextOptions = options.slice(0, nextLength);
              while (nextOptions.length < nextLength) nextOptions.push('');
              updateOptions(nextOptions);
            }}
          >
            {[2, 3, 4, 5, 6, 7, 8, 9, 10].map((count) => (
              <MenuItem key={count} value={count}>
                {count}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
      {options.slice(0, optionCount).map((option, index) => (
        <div key={index} className={styles.builderOptionRow}>
          {selectionMode === 'single' ? (
            <Radio
              checked={Number(draft.answer ?? 0) === index}
              onChange={() => onPatch({ answer: index })}
            />
          ) : (
            <Checkbox
              checked={selectedAnswers.includes(String(index))}
              onChange={() => {
                const next = new Set(selectedAnswers);
                if (next.has(String(index))) next.delete(String(index));
                else next.add(String(index));
                onPatch({ answer: Array.from(next) });
              }}
            />
          )}
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            label={`Option ${String.fromCharCode(65 + index)}`}
            InputLabelProps={{ shrink: true }}
            value={String(option || '')}
            onChange={(e) => {
              const nextOptions = [...options];
              nextOptions[index] = e.target.value;
              updateOptions(nextOptions);
            }}
          />
        </div>
      ))}
    </BuilderSectionCard>
  );
}

function AudioUploadPanel({
  draft,
  onPatch,
  setButtonDisabled,
  showDuration = true,
}: {
  draft: BuilderQuestion;
  onPatch: (patch: Partial<BuilderQuestion>) => void;
  setButtonDisabled?: (value: boolean) => void;
  showDuration?: boolean;
}) {
  return (
    <BuilderSectionCard title="Audio">
      {showDuration ? (
        <FormControl fullWidth size="small" sx={{ mb: 2, maxWidth: 220 }}>
          <InputLabel>Time</InputLabel>
          <Select
            value={draft.duration ?? 60}
            label="Time"
            onChange={(e) => onPatch({ duration: e.target.value })}
          >
            {DURATIONS.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      ) : null}
      <div className={styles.builderMediaStack}>
        <AudioRecorderAny
          handleAudio={(value) => onPatch({ audio: value })}
          index={0}
          currentSubmission={{}}
          refresh={() => {}}
          source={draft.audio}
          hasSaveButton
          message="Record question audio"
          setButtonDisabled={(value) => setButtonDisabled?.(value)}
          showResponsePreview={false}
        />
        <div className={styles.builderOr}>OR</div>
        <AudioPickerAny
          onChange={(value) => onPatch({ audio: value })}
          style={{}}
          message="Upload a audio file for question..."
          setButtonDisabled={(value) => setButtonDisabled?.(value)}
          showResponsePreview={false}
        />
      </div>
    </BuilderSectionCard>
  );
}

function EssayQuestionBuilder(props: QuestionBuilderProps) {
  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
    </div>
  );
}

function MultipleChoiceQuestionBuilder(props: QuestionBuilderProps) {
  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
      <ChoiceOptionsEditor draft={props.draft} onPatch={props.onPatch} selectionMode="single" />
    </div>
  );
}

function MultipleResponseQuestionBuilder(props: QuestionBuilderProps) {
  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
      <ChoiceOptionsEditor draft={props.draft} onPatch={props.onPatch} selectionMode="multiple" />
    </div>
  );
}

function TrueFalseQuestionBuilder(props: QuestionBuilderProps) {
  const options = Array.isArray(props.draft.options) && props.draft.options.length ? props.draft.options : ['Cierto', 'Falso'];
  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
      <ChoiceOptionsEditor draft={{ ...props.draft, options }} onPatch={props.onPatch} selectionMode="single" fixedLength={2} />
    </div>
  );
}

function AudioQuestionBuilder(props: QuestionBuilderProps) {
  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
      <AudioUploadPanel draft={props.draft} onPatch={props.onPatch} setButtonDisabled={props.setButtonDisabled} />
    </div>
  );
}

function AudioTrueFalseQuestionBuilder(props: QuestionBuilderProps) {
  const options = Array.isArray(props.draft.options) && props.draft.options.length ? props.draft.options : ['Cierto', 'Falso'];
  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
      <AudioUploadPanel draft={props.draft} onPatch={props.onPatch} setButtonDisabled={props.setButtonDisabled} />
      <ChoiceOptionsEditor draft={{ ...props.draft, options }} onPatch={props.onPatch} selectionMode="single" fixedLength={2} />
    </div>
  );
}

function AudioToImageQuestionBuilder(props: QuestionBuilderProps) {
  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
      <AudioUploadPanel draft={props.draft} onPatch={props.onPatch} setButtonDisabled={props.setButtonDisabled} />
      <BuilderSectionCard title="Student Upload Limit">
        <FormControl fullWidth size="small" sx={{ maxWidth: 260 }}>
          <InputLabel>Student can upload images</InputLabel>
          <Select
            value={props.draft.studentCanUploadImages || 6}
            label="Student can upload images"
            onChange={(e) => props.onPatch({ studentCanUploadImages: e.target.value, categoryType: 'Image' })}
          >
            {imageUpload?.map((item) => (
              <MenuItem key={item.value} value={item.value}>
                {item.value}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </BuilderSectionCard>
      {/* <BuilderSectionCard title="">
        <PictureAssignmentAny
          disabled={true}
          source={props.draft.image}
          handlePicture={(value: string) => props.onPatch({ image: value })}
          handleAdditionalPictures={() => {}}
          path=""
          type=""
          showImagePicker={false}
          isNewSubmission={false}
          showResponsePreview={false}
        />
      </BuilderSectionCard> */}
    </div>
  );
}

function PictureQuestionBuilder(props: QuestionBuilderProps) {
  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
      <BuilderSectionCard title="Upload Media">
        <PictureAssignmentAny
          source={props.draft.pictures}
          additionalImages={props.draft.additionalPictures || []}
          handlePicture={(value: string) => props.onPatch({ pictures: value })}
          handleAdditionalPictures={(value: string[]) => props.onPatch({ additionalPictures: value })}
          path=""
          type=""
          showImagePicker={false}
          isNewSubmission={false}
          containerClassName="assignment-ebook"
          imageClassName="assignment-image"
          setButtonDisabled={(value: boolean) => props.setButtonDisabled?.(value)}
          showResponsePreview={false}
        />
      </BuilderSectionCard>
      <BuilderSectionCard title="">
        <PictureAssignmentTypeAny
          states={{ question: props.draft, index: 0 }}
          funcObj={{
            onChangeQuestionData: (value: any, key: string) => props.onPatch({ [key]: value }),
          }}
          showResponsePreview={false}
        />
      </BuilderSectionCard>
    </div>
  );
}

function VideoQuestionBuilder(props: QuestionBuilderProps) {
  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
      <BuilderSectionCard title="Video Setup">
        <AssignmentVideoAny
          states={{
            index: 0,
            question: props.draft,
            isAssigned: false,
            create_new_copy: false,
            assignedloading: false,
            showRemoveVideoButton: true,
          }}
          funcObj={{
            onChangeQuestionData: (value: any, key: string) => props.onPatch({ [key]: value }),
            setButtonDisabled: (value: boolean) => props.setButtonDisabled?.(value),
          }}
          showResponsePreview={false}
        />
      </BuilderSectionCard>
    </div>
  );
}

function MatchQuestionBuilder(props: QuestionBuilderProps) {
  const optionRefs = useRef({});

  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
      <BuilderSectionCard title="Match Builder">
        <div className={styles.matchMakingEmbed}>
        <MatchMakingAny
          disabled={false}
          index={0}
          question={props.draft}
          optionRefs={optionRefs}
          onChangeType={(value: unknown, key: string) => {
            if (key === 'questionType') {
              props.onPatch({
                questionType: value,
                options: [{ item: '', match_item: '', match_audio: '', match_text: '', questionImage: '', questionAudio: '' }],
              });
              return;
            }
            if (key === 'matchType') {
              props.onPatch({ matchType: value });
              return;
            }
            props.onPatch({ [key]: value });
          }}
          onAddMoreOption={() => {
            props.setDraft((current) => {
              if (!current) return current;
              const prev = Array.isArray(current.options) ? current.options : [];
              const nextOptions = [...prev, { item: '', match_item: '', match_audio: '', match_text: '', questionImage: '', questionAudio: '' }];
              return { ...current, options: nextOptions };
            });
          }}
          onchangeOptionMatch={(_: number, optionIndex: number, value: unknown, key: string) => {
            props.setDraft((current) => {
              if (!current) return current;
              const prev = Array.isArray(current.options) ? current.options : [];
              const nextOptions = [...prev];
              const raw = prev[optionIndex];
              const existingOption =
                raw != null && typeof raw === 'object' && !Array.isArray(raw) ? { ...(raw as Record<string, unknown>) } : {};
              nextOptions[optionIndex] = { ...existingOption, [key]: value };
              return { ...current, options: nextOptions };
            });
          }}
          onDeleteMatchOption={(_: number, optionIndex: number) => {
            props.setDraft((current) => {
              if (!current) return current;
              const prev = Array.isArray(current.options) ? current.options : [];
              return { ...current, options: prev.filter((_: unknown, idx: number) => idx !== optionIndex) };
            });
          }}
          loading={false}
          colorSchema=""
          setButtonDisabled={(value: boolean) => props.setButtonDisabled?.(value)}
          isEditing={Boolean(props.draft.id)}
          showImagePicker={false}
          showResponsePreview={false}
        />
        </div>
      </BuilderSectionCard>
    </div>
  );
}

function checkHighLightWithTyping(updatedQuestions: BuilderQuestion[], index: number, optId: number, value: string) {
  try {
    if (value?.trim()?.length > 0) {
      if (!updatedQuestions[index].highlightWords) {
        updatedQuestions[index].highlightWords = [];
      }
      if (!updatedQuestions[index].highlightWords[optId]) {
        updatedQuestions[index].highlightWords[optId] = [];
      }
      const highlightWords = updatedQuestions[index].highlightWords[optId];
      const optionText = value;

      const filterHighlightedWord =
        highlightWords?.filter((data: { text?: string }) => {
          if (optionText?.includes(data?.text || '')) {
            return true;
          }
          return false;
        }) || [];
      updatedQuestions[index].highlightWords[optId] = filterHighlightedWord;

      if (updatedQuestions[index].answerSelected[optId]?.length > 0) {
        const customAnswerSelected = updatedQuestions[index].answerSelected[optId];
        const filterCustomAnswer = customAnswerSelected?.filter((data: { type?: string }) => data.type === 'custom') || [];

        const finalAnswerResult = [...new Set([...filterCustomAnswer, ...filterHighlightedWord])];
        updatedQuestions[index].answerSelected[optId] = finalAnswerResult;
      }
    }
    return updatedQuestions;
  } catch {
    return updatedQuestions;
  }
}

function FillInBlanksQuestionBuilder({ imageMode = false, ...props }: QuestionBuilderProps & { imageMode?: boolean }) {
  const dispatch = useDispatch();
  const { setAlertLoading, showAlert } = useAlertLoading();
  const answerRefs = useRef([]);
  const optionRefs = useRef({});
  const draftRef = useRef<BuilderQuestion | null>(null);
  const [optionStates, setOptionStates] = useState<Record<string, any>>({});
  const [inputValue, setInputValue] = useState<Record<string, string>>({});

  const draftQuestion = useMemo(() => {
    if (imageMode && props.draft.type !== 'fillingImage') {
      return { ...props.draft, type: 'fillingImage' };
    }
    return props.draft;
  }, [imageMode, props.draft]);

  useEffect(() => {
    if (draftQuestion) {
      draftRef.current = draftQuestion;
    }
  }, [draftQuestion]);

  const updateDraft = (updater: (current: BuilderQuestion) => BuilderQuestion) => {
    props.setDraft((current) => {
      if (!current) return current;
      const next = updater(cloneDeep(current));
      draftRef.current = next;
      return next;
    });
  };

  const handleInputChange = (index: number, optId: number, value: string) => {
    setInputValue((prev) => ({ ...prev, [`${index}-${optId}`]: value }));
  };

  const getInputValue = (index: number, optId: number) => inputValue[`${index}-${optId}`] || '';

  const onchangeOptionFilling = useCallback(
    (index: number, optId: number, value: string) => {
      try {
        const base = draftRef.current;
        if (!base) return;
        const updatedQuestions = [cloneDeep(base)];

        if (!updatedQuestions[index]) {
          updatedQuestions[index] = {
            type: base.type || 'filling',
            options: [],
            answerSelected: [],
            highlightWords: [],
          } as BuilderQuestion;
        }

        if (!updatedQuestions[index].options) {
          updatedQuestions[index].options = [];
        }

        if (!updatedQuestions[index].answerSelected) {
          updatedQuestions[index].answerSelected = [];
        }

        if (updatedQuestions[index].answerSelected && value?.trim()?.length === 0) {
          updatedQuestions[index].answerSelected[optId] = [];
          if (updatedQuestions[index].highlightWords) {
            updatedQuestions[index].highlightWords[optId] = [];
          }
        }
        const filterUpdatedQuestion = checkHighLightWithTyping(updatedQuestions, index, optId, value);
        filterUpdatedQuestion[index].options[optId] = value || '';

        const currentQuestion = draftRef.current;
        if (currentQuestion && currentQuestion.question !== undefined) {
          filterUpdatedQuestion[index].question = currentQuestion.question;
        }

        const nextQ = filterUpdatedQuestion[index];
        draftRef.current = nextQ;
        props.setDraft(nextQ);
      } catch {
        return;
      }
    },
    [props]
  );

  const onDeleteOption = (_index: number, optId: number) => {
    updateDraft((current) => {
      current.options = (current.options || []).filter((_: unknown, idx: number) => idx !== optId);
      current.answerSelected = (current.answerSelected || []).filter((_: unknown, idx: number) => idx !== optId);
      current.highlightWords = (current.highlightWords || []).filter((_: unknown, idx: number) => idx !== optId);
      current.filling_image = (current.filling_image || []).filter((_: unknown, idx: number) => idx !== optId);
      return current;
    });
  };

  const onAddOption = (_index: number) => {
    updateDraft((current) => {
      const u = current;
      if (!u.options) {
        u.options = [''];
      }
      if (!u.answerSelected) {
        u.answerSelected = [''];
      }
      if (!u.filling_image) {
        u.filling_image = [{ url: '' }];
      }
      if (!u.highlightWords) {
        u.highlightWords = [[{ start: '', end: '', text: '' }]];
      }

      u.options.push('');
      u.answerSelected.push('');
      u.highlightWords.push([
        {
          start: '',
          end: '',
          text: '',
        },
      ]);

      if (u.fillingType === 'Image' || u.fillingType === 'Audio') {
        u.filling_image.push({ url: '' });
      }
      return u;
    });
  };

  const onUpdateAnswer = (index: number, optId: number, value: any[]) => {
    try {
      const base = draftRef.current;
      if (!base) return;
      const updatedQuestions = [cloneDeep(base)];

      if (!updatedQuestions[index]) {
        updatedQuestions[index] = {
          type: base.type || 'filling',
          options: [],
          answerSelected: [],
        } as BuilderQuestion;
      }

      if (!updatedQuestions[index].answerSelected) {
        updatedQuestions[index].answerSelected = [];
      }

      const normalizedAnswers = value.map((item) => ({
        start: item.start ?? null,
        end: item.end ?? null,
        text: item.text,
        id: item.id,
        type: item.type || 'custom',
      }));

      const seenCustomTexts = new Set<string>();
      const uniqueAnswers: any[] = [];

      normalizedAnswers.forEach((item) => {
        const normalizedText = item.text?.toLowerCase()?.trim();

        if (item.type === 'highlight') {
          uniqueAnswers.push(item);
        } else {
          const hasHighlight = uniqueAnswers.some(
            (existing) => existing.type === 'highlight' && existing.text?.toLowerCase()?.trim() === normalizedText
          );

          if (!seenCustomTexts.has(normalizedText || '') && !hasHighlight) {
            seenCustomTexts.add(normalizedText || '');
            uniqueAnswers.push(item);
          }
        }
      });

      updatedQuestions[index].answerSelected[optId] = uniqueAnswers;

      if (updatedQuestions[index].highlightWords) {
        const highlightWordsData = updatedQuestions[index].highlightWords[optId];
        const highlightIds = new Set(
          uniqueAnswers.filter((answer) => answer.type === 'highlight').map((answer) => answer.id)
        );

        const filterHighlightedData = highlightWordsData?.filter((x: { type?: string; id?: number }) => {
          if (x?.type === 'highlight' && x?.id !== undefined) {
            return highlightIds.has(x.id);
          }
          return false;
        });

        updatedQuestions[index].highlightWords[optId] = filterHighlightedData;
      }
      const nextQ = updatedQuestions[index];
      draftRef.current = nextQ;
      props.setDraft(nextQ);
    } catch {
      return;
    }
  };

  const makeAnswerSubmitHandler = (index: number, optId: number, highlightWords: any[]) => {
    try {
      const base = draftRef.current;
      if (!base) return;
      const updatedQuestions = [cloneDeep(base)];
      if (!updatedQuestions[index].answerSelected) {
        updatedQuestions[index].answerSelected = [];
      }
      if (!updatedQuestions[index].options) {
        updatedQuestions[index].options = [];
      }
      if (!updatedQuestions[index].highlightWords) {
        updatedQuestions[index].highlightWords = [];
      }

      const normalizedHighlightWords = highlightWords.map((item) => ({
        start: item.start ?? null,
        end: item.end ?? null,
        text: item.text,
        id: item.id,
        type: item.type || 'highlight',
      }));

      updatedQuestions[index].highlightWords[optId] = normalizedHighlightWords;
      const currentHighLightedWord = updatedQuestions[index].highlightWords[optId];
      const currentAnswerSelected = updatedQuestions[index].answerSelected[optId] || [];

      const normalizedCustomAnswers =
        currentAnswerSelected
          ?.filter((x: { type?: string }) => x.type === 'custom')
          .map((item: any) => ({
            start: item.start ?? null,
            end: item.end ?? null,
            text: item.text,
            id: item.id,
            type: 'custom',
          })) || [];

      const deduplicatedCustomAnswers = removeCustomDuplicates(normalizedCustomAnswers);
      const finalAnswer = [...deduplicatedCustomAnswers, ...currentHighLightedWord];
      const result = filterDuplicateWords(finalAnswer);

      const normalizedResult = result.map((item) => ({
        start: item.start ?? null,
        end: item.end ?? null,
        text: item.text,
        id: item.id,
        type: item.type || 'custom',
      }));

      updatedQuestions[index].answerSelected[optId] = normalizedResult;
      const nextQ = updatedQuestions[index];
      draftRef.current = nextQ;
      props.setDraft(nextQ);
    } catch {
      return;
    }
  };

  const generateAIHandler = async ({ text, index, optId }: { text: string; index: number; optId: number }) => {
    if (!text?.length) {
      showAlert('Please provide option input', 'error');
      return;
    }
    try {
      setAlertLoading(true, 'Processing your request...');
      const base = draftRef.current;
      if (!base) return;
      const updatedQuestions = [cloneDeep(base)];
      const response: any = await (dispatch as (a: unknown) => Promise<unknown>)(
        handleGetAiOptionForAnswer({
          option_text: text,
        })
      );
      if (!response?.data) {
        showAlert('AI is not able to generate answer.', 'error');
        return;
      }
      if (!Array.isArray(response?.data[0])) {
        showAlert('AI is not able to generate answer.', 'error');
        return;
      }
      if (!updatedQuestions[index]?.answerSelected) {
        updatedQuestions[index].answerSelected = [];
      }
      const data = updatedQuestions[index].answerSelected[optId];

      if (!data) {
        updatedQuestions[index].answerSelected[optId] = [...new Set(response?.data[0])];
      } else {
        const mergedData = removeCustomDuplicates([...data, ...response?.data[0]]);
        updatedQuestions[index].answerSelected[optId] = [...new Set(mergedData)];
      }

      const nextQ = updatedQuestions[index];
      draftRef.current = nextQ;
      props.setDraft(nextQ);
    } catch {
      showAlert('AI is not able to generate answer.', 'error');
    } finally {
      setAlertLoading(false, '');
    }
  };

  const handlePictureFilling = (_index: number, optId: number, value: string) => {
    updateDraft((current) => {
      if (!Array.isArray(current.filling_image)) current.filling_image = [];
      current.filling_image[optId] = { url: value };
      return current;
    });
  };

  const toggleDropdown = (optId: number, isOpen: boolean, questionType: string, questionIndex: number) => {
    setOptionStates((prev) => ({
      ...prev,
      [optId]: {
        ...prev[optId],
        questionIndex,
        type: questionType,
        dropdownOpen: isOpen,
      },
    }));
  };

  const toggleEditor = (optId: number, isOpen: boolean, questionType: string, questionIndex: number) => {
    setOptionStates((prev) => ({
      ...prev,
      [optId]: {
        ...prev[optId],
        questionIndex,
        type: questionType,
        editorOpen: isOpen,
      },
    }));
  };

  const SelectAnswerDropDown = ({
    questionIndex,
    optId,
    optionText,
    questionType,
  }: {
    questionIndex: number;
    optId: number;
    optionText: string;
    questionType: string;
  }) => ({
    items: [
      {
        key: 'selectAnswer',
        label: (
          <div onClick={() => toggleEditor(optId, true, questionType, questionIndex)}>Select Answer from option</div>
        ),
      },
      {
        key: 'selectRandomAnswer',
        label: (
          <div
            onClick={() => {
              toggleEditor(optId, false, questionType, questionIndex);
              generateAIHandler({ text: optionText, index: questionIndex, optId });
            }}
          >
            Select Random Answer with AI
          </div>
        ),
      },
    ],
  });

  const questionsList = useMemo(() => [draftQuestion], [draftQuestion]);

  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={draftQuestion} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={draftQuestion} onPatch={props.onPatch} />
      <BuilderSectionCard title="Fill In The Blanks Setup">
        <FillInBlanksAny
          showResponsePreview={false}
          refs={{ answerRefs, optionRefs }}
          states={{
            index: 0,
            question: draftQuestion,
            questions: questionsList,
            optionStates,
            isEditing: Boolean(draftQuestion.id),
          }}
          funcObj={{
            onAddOption,
            onchangeOptionFilling,
            onDeleteOption,
            handleInputChange,
            onUpdateAnswer,
            getInputValue,
            makeAnswerSubmitHandler,
            handlePictureFilling,
            toggleDropdown,
            toggleEditor,
            setButtonDisabled: (value: boolean) => props.setButtonDisabled?.(value),
            onChangeQuestionData: (value: any, key: string, _index?: number) => {
              if (key === 'fillingType') {
                props.onPatch({ [key]: value, filling_image: [{ url: '' }] });
                return;
              }
              props.onPatch({ [key]: value });
            },
          }}
          SelectAnswerDropDown={SelectAnswerDropDown}
          disabled={false}
          embedInMuiModal
        />
      </BuilderSectionCard>
    </div>
  );
}

function PhotoCollageQuestionBuilder(props: QuestionBuilderProps) {
  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <QuestionRichTextField draft={props.draft} onPatch={props.onPatch} />
      <BuilderSectionCard title="Collage Setup">
        <AssignmentPhotoCollageAny
          states={{
            index: 0,
            question: props.draft,
            isPreview: true,
            selectData: true,
            collageLength: props.draft?.collageLength,
            frameCollage: props.draft?.collageFrame,
            showFullPreview: true,
          }}
          funcObj={{
            onChangeQuestionData: (value: any, key: string) => props.onPatch({ [key]: value }),
          }}
          showResponsePreview={false}
        />
      </BuilderSectionCard>
    </div>
  );
}

function getGroupedQuestions(draft: BuilderQuestion, type: string) {
  const items = Array.isArray(draft.subQuestions) && draft.subQuestions.length ? draft.subQuestions : [draft];
  return items.map((item, index) => ({
    ...cloneDeep(item),
    type,
    groupKey: item.groupKey || draft.groupKey,
    _uid: item._uid || `${draft._uid || 'group'}-${index}`,
  }));
}

function AudioMultipleChoiceQuestionBuilder(props: QuestionBuilderProps) {
  const questions = getGroupedQuestions(props.draft, 'audioMultipleChoice');

  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <BuilderSectionCard title="Audio Multiple Choice Group">
        <AudioMultipleChoice
          questions={questions}
          groupKey={props.draft.groupKey}
          index={0}
          setButtonDisabled={(value: boolean) => props.setButtonDisabled?.(value)}
          setQuestions={(nextQuestions: BuilderQuestion[]) => {
            props.setDraft((current) => {
              if (!current) return current;
              const first = nextQuestions[0] || current;
              return {
                ...current,
                ...first,
                subQuestions: nextQuestions,
              };
            });
          }}
        />
      </BuilderSectionCard>
    </div>
  );
}

function AudioEssayQuestionBuilder(props: QuestionBuilderProps) {
  const questions = getGroupedQuestions(props.draft, 'audioEssay');

  return (
    <div className={styles.builderStack}>
      <QuestionTypeSelector draft={props.draft} onTypeChange={props.onTypeChange} />
      <BuilderSectionCard title="Audio Written Response Group">
        <AudioWithEssay
          showResponsePreview={false}
          questions={questions}
          groupKey={props.draft.groupKey}
          index={0}
          setButtonDisabled={(value: boolean) => props.setButtonDisabled?.(value)}
          setQuestions={(nextQuestions: BuilderQuestion[]) => {
            props.setDraft((current) => {
              if (!current) return current;
              const first = nextQuestions[0] || current;
              return {
                ...current,
                ...first,
                subQuestions: nextQuestions,
              };
            });
          }}
        />
      </BuilderSectionCard>
    </div>
  );
}

export function QuestionBuilderRenderer(props: QuestionBuilderProps) {
  const type = props.draft.type;

  let inner: React.ReactNode;
  if (type === 'multipleChoice') inner = <MultipleChoiceQuestionBuilder {...props} />;
  else if (type === 'multipleResponse') inner = <MultipleResponseQuestionBuilder {...props} />;
  else if (type === 'trueFalse') inner = <TrueFalseQuestionBuilder {...props} />;
  else if (type === 'audio') inner = <AudioQuestionBuilder {...props} />;
  else if (type === 'audioTrueFalse') inner = <AudioTrueFalseQuestionBuilder {...props} />;
  else if (type === 'audioToImage') inner = <AudioToImageQuestionBuilder {...props} />;
  else if (type === 'audioMultipleChoice') inner = <AudioMultipleChoiceQuestionBuilder {...props} />;
  else if (type === 'audioEssay') inner = <AudioEssayQuestionBuilder {...props} />;
  else if (type === 'picture') inner = <PictureQuestionBuilder {...props} />;
  else if (type === 'video') inner = <VideoQuestionBuilder {...props} />;
  else if (type === 'match') inner = <MatchQuestionBuilder {...props} />;
  else if (type === 'filling') inner = <FillInBlanksQuestionBuilder {...props} />;
  else if (type === 'fillingImage') inner = <FillInBlanksQuestionBuilder {...props} imageMode />;
  else if (type === 'photoCollage') inner = <PhotoCollageQuestionBuilder {...props} />;
  else inner = <EssayQuestionBuilder {...props} />;

  return (
    <>
      {inner}
      <SuperAdminQuestionExtras
        draft={props.draft as Record<string, unknown> & { type: string }}
        onPatch={props.onPatch as (patch: Partial<Record<string, unknown> & { type: string }>) => void}
        superAdminMode={props.superAdminMode}
        bookPages={props.bookPages}
        isStandardExamBook={props.isStandardExamBook}
        standards={props.standards}
        ebookGradeLevels={props.ebookGradeLevels}
        enabledStateCodes={props.enabledStateCodes}
      />
    </>
  );
}
